// The Lender Hub's routes in the portal (plan v2 §3.4). The Hub has no "kind" on a route, so a person picks it once
// (dedupe / prefill / status / other); the workflow's lender API node then offers lender → kind → route. A kind
// that changes the node's ports cannot be picked while a live workflow version uses the route.
import { lenderApiPorts, NodeType, type RoadBlock, type RouteKind } from '../../schemas'
import { refuse } from '../refuse'
import type { HubRouteRecord, State } from '../state'

const LENDER_NODE = NodeType.enum['campaign.http_preset']

/** "Name" v2 (live) for every workflow version (live or draft) whose lender API node calls this route. */
export function routeUsedBy(state: State, routeId: string): { label: string; live: boolean }[] {
  return state.versions
    .filter((v) => v.status !== 'retired')
    .filter((v) => v.document.nodes.some((n) => n.type === LENDER_NODE && n.cfg.route === routeId))
    .map((v) => {
      const name = state.workflows.find((w) => w.id === v.workflow_id)?.name ?? v.workflow_id
      return { label: `"${name}" v${v.version} (${v.status})`, live: v.status === 'live' }
    })
}

/** True when the route's outcomes include redirect: the lender's code can give the person a link. */
export function givesLink(route: HubRouteRecord): boolean {
  return route.outcomes.includes('redirect')
}

/** R1 holds for every route; R3 for a prefill route that never answers with a link. */
export function roadBlocksOf(route: HubRouteRecord): RoadBlock[] {
  return route.kind === 'prefill' && !givesLink(route) ? ['R1', 'R3'] : ['R1']
}

const samePorts = (a: RouteKind | null, b: RouteKind | null) =>
  lenderApiPorts(a).join() === lenderApiPorts(b).join()

export function setRouteKind(state: State, route: HubRouteRecord, kind: RouteKind): void {
  if (route.kind === kind) refuse('That is its kind already.')
  // a route without a kind is in no workflow yet, so only a picked kind can be in use
  const live = routeUsedBy(state, route.id).find((use) => use.live)
  if (route.kind && live && !samePorts(route.kind, kind))
    refuse(
      `The live version ${live.label} uses this route: as "${kind}" it would leave by other ports. Take it out in a new draft first.`
    )
  route.kind = kind
}

/** Lender · Kind · POST /path, the way pickers and traces name a route. */
export function routeLabel(
  state: State,
  route: HubRouteRecord,
  kindLabel: (kind: RouteKind) => string
): string {
  const lender = state.hubLenders.find((l) => l.slug === route.lender_slug)?.name ?? route.lender_slug
  const kind = route.kind ? kindLabel(route.kind) : 'no kind yet'
  return `${lender} · ${kind} · ${route.method} ${route.path}`
}
